import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Download, Upload, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "usl_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

export default function Recipients() {
  const [items, setItems] = useState([]);
  const [single, setSingle] = useState({ name: "", address_line1: "", town: "", postcode: "", campaign_code: "" });
  const [csv, setCsv] = useState("");
  const [csvCampaign, setCsvCampaign] = useState("");
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/recipients`, { headers: authHeader() }); setItems(data); }
    catch { toast.error("Failed to load."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSingle = async (e) => {
    e.preventDefault();
    if (!single.name || !single.address_line1 || !single.postcode) { toast.error("Name, address and postcode required"); return; }
    try {
      await axios.post(`${API}/admin/recipients`, single, { headers: authHeader() });
      toast.success("Recipient added");
      setSingle({ name: "", address_line1: "", town: "", postcode: "", campaign_code: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const bulkAdd = async () => {
    if (!csv.trim()) { toast.error("Paste CSV first"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/recipients/bulk`, { csv_text: csv, campaign_code: csvCampaign }, { headers: authHeader() });
      toast.success(`Added ${data.created}, skipped ${data.skipped}`);
      setCsv(""); setCsvCampaign("");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete recipient "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/recipients/${id}`, { headers: authHeader() }); load(); }
    catch { toast.error("Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/recipients/${id}?status=${status}`, {}, { headers: authHeader() }); load(); }
    catch { toast.error("Failed"); }
  };

  const exportMailMerge = () => {
    const origin = window.location.origin;
    const header = ["name","address_line1","town","postcode","code","response_url","callback_url","qr_url","campaign_code","status"];
    const rows = filtered.map(r => header.map(h => {
      if (h === "response_url") return `"${origin}/respond?code=${r.code}${r.campaign_code ? `&c=${r.campaign_code}` : ""}"`;
      if (h === "callback_url") return `"${origin}/book-a-call?code=${r.code}${r.campaign_code ? `&c=${r.campaign_code}` : ""}"`;
      if (h === "qr_url") return `"${BACKEND}/api/qr.png?code=${r.code}${r.campaign_code ? `&c=${r.campaign_code}` : ""}&size=12"`;
      return `"${String(r[h] ?? "").replace(/"/g, '""')}"`;
    }).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `upsurge-mailmerge-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadQr = async (r) => {
    try {
      const res = await axios.get(`${API}/admin/recipients/${r.id}/qr.png?size=12`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `qr-${r.code}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`QR code for ${r.code} downloaded`);
    } catch { toast.error("Failed to generate QR code"); }
  };

  const filtered = filter === "All" ? items : items.filter(i => i.status === filter);

  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Single add */}
        <form data-testid="recipient-single-form" onSubmit={addSingle} className="bg-[#11171A] border border-[#F4EFE6]/10 p-6 space-y-4">
          <div className="overline">Add one</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="overline block mb-1">Name</label><input data-testid="recipient-name" className="underline-input" value={single.name} onChange={(e) => setSingle({ ...single, name: e.target.value })} /></div>
            <div className="col-span-2"><label className="overline block mb-1">Address line 1</label><input data-testid="recipient-address" className="underline-input" value={single.address_line1} onChange={(e) => setSingle({ ...single, address_line1: e.target.value })} /></div>
            <div><label className="overline block mb-1">Town</label><input data-testid="recipient-town" className="underline-input" value={single.town} onChange={(e) => setSingle({ ...single, town: e.target.value })} /></div>
            <div><label className="overline block mb-1">Postcode</label><input data-testid="recipient-postcode" className="underline-input uppercase" value={single.postcode} onChange={(e) => setSingle({ ...single, postcode: e.target.value })} /></div>
            <div className="col-span-2"><label className="overline block mb-1">Campaign code (optional)</label><input data-testid="recipient-campaign" className="underline-input uppercase tracking-wider" value={single.campaign_code} onChange={(e) => setSingle({ ...single, campaign_code: e.target.value })} placeholder="CAMP-XXXXX" /></div>
          </div>
          <button data-testid="recipient-add" type="submit" className="usl-btn text-xs"><Plus size={14} /> Add Recipient</button>
        </form>

        {/* Bulk add */}
        <div data-testid="recipient-bulk-form" className="bg-[#11171A] border border-[#F4EFE6]/10 p-6 space-y-4">
          <div className="overline">Bulk import (paste CSV)</div>
          <div>
            <label className="overline block mb-1">Campaign code for all (optional)</label>
            <input data-testid="recipient-bulk-campaign" className="underline-input uppercase tracking-wider" value={csvCampaign} onChange={(e) => setCsvCampaign(e.target.value)} placeholder="CAMP-XXXXX" />
          </div>
          <div>
            <label className="overline block mb-1">CSV (columns: name, address, town, postcode)</label>
            <textarea data-testid="recipient-csv" rows={6} className="underline-input resize-y" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={"John Doe,The Old Farm,Stow,GL54 1AB\nJane Smith,Manor House,Bourton,GL54 2CD"} style={{ fontFamily: 'ui-monospace, monospace', fontSize: '12px' }} />
          </div>
          <button data-testid="recipient-bulk-add" onClick={bulkAdd} className="usl-btn text-xs"><Upload size={14} /> Import</button>
        </div>
      </div>

      {/* Filters & export */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="overline mr-2">Status:</div>
        {["All", "Queued", "Sent", "Responded", "Suppressed"].map(s => (
          <button key={s} data-testid={`recipient-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${filter === s ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"}`}
          >{s}</button>
        ))}
        <div className="ml-auto">
          <button data-testid="recipient-export" onClick={exportMailMerge} className="usl-btn text-xs"><Download size={14} /> Export Mail-Merge CSV</button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold border-b border-[#F4EFE6]/10">
          <div className="col-span-3">Name &amp; Address</div>
          <div className="col-span-2">Postcode</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-2">Campaign</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 ? (
          <div data-testid="recipients-empty" className="p-10 text-center text-[#9BA39B] text-sm">No recipients.</div>
        ) : filtered.map(r => (
          <div key={r.id} data-testid={`recipient-row-${r.id}`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm border-b border-[#F4EFE6]/10">
            <div className="col-span-3">
              <div className="text-[#F4EFE6] font-semibold">{r.name}</div>
              <div className="text-[11px] text-[#9BA39B]">{r.address_line1}{r.town ? `, ${r.town}` : ""}</div>
            </div>
            <div className="col-span-2 text-[#E8E1D2] uppercase">{r.postcode}</div>
            <div className="col-span-2 font-mono text-[#C9A24B] tracking-wider text-[12px]">{r.code}</div>
            <div className="col-span-2 font-mono text-[#9BA39B] text-[12px]">{r.campaign_code || "—"}</div>
            <div className="col-span-2">
              <select className="bg-transparent border border-[#F4EFE6]/15 text-[#E8E1D2] text-[11px] tracking-wider px-2 py-1 uppercase" value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`recipient-status-${r.id}`}>
                {["Queued","Sent","Responded","Suppressed"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-1 text-right flex items-center justify-end gap-1">
              <button data-testid={`recipient-qr-${r.id}`} onClick={() => downloadQr(r)} className="text-[#9BA39B] hover:text-[#C9A24B] p-1.5" title="Download QR code"><QrCode size={16} /></button>
              <button data-testid={`recipient-delete-${r.id}`} onClick={() => remove(r.id, r.name)} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#9BA39B]">Mail-merge CSV exports each recipient with unique <code className="text-[#C9A24B]">response_url</code>, <code className="text-[#C9A24B]">callback_url</code> and <code className="text-[#C9A24B]">qr_url</code> columns — paste into Microsoft Word or any mailmerge tool to print personalised letters with a scannable QR code. Click the <span className="inline-flex items-center gap-1 align-middle"><QrCode size={11} className="text-[#C9A24B]" /></span> icon on any row to download that recipient&apos;s QR as a print-ready PNG.</p>
    </div>
  );
}
